/**
 * [공통] 다국어 지원
 * @module @helpers/i18n
 */
import moment from 'moment'

const dateFormat = 'YYYY.MM.DD'

export const datetimeFormats = {
  en: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
    long24: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  ko: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    },
    long24: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
  ja: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    },
    long24: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    },
  },
}

/**
 * @alias module:@helpers/i18n
 * @method toLocaleDateString
 * @param {object.<date>} at UTC 날짜
 * @returns {object.<date>} 현지화: 날짜
 */
export const toLocaleDateString = (at) => moment(at).format(dateFormat)

export const toLocaleDateStringWithFormat = (at, format) => moment(at).format(format)

/**
 * @alias module:@helpers/i18n
 * @method toLocaleTimeString
 * @param {object.<date>} at UTC 날짜
 * @returns {object.<date>} 현지화: 시간
 */
export const toLocaleTimeString = (at) => {
  const options = {
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  }
  return new Date(at).toLocaleTimeString([], options)
}

/**
 * @alias module:@helpers/i18n
 * @method compareDate
 * @param {object.<date>} startAt UTC 시작 날짜
 * @param {object.<date>} endAt UTC 종료 날짜
 * @returns {object.<boolean, string, string, string, string>} 시작일과 종료일 동일 여부, 시작일, 시작시간, 종료일, 종료시간
 */
export const compareDate = (startAt, endAt) => {
  const startDate = toLocaleDateString(startAt)
  const startTime = toLocaleTimeString(startAt)
  const endDate = toLocaleDateString(endAt)
  const endTime = toLocaleTimeString(endAt)
  return {
    isOnedayClass: startDate === endDate,
    startDate,
    startTime,
    endDate,
    endTime,
  }
}

/**
 * @alias module:@helpers/i18n
 * @method displayPeriod
 * @param {*} param0 당일교육 여부, 시작일시, 종료일시,
 * @returns {string} 당일교육 날짜 또는 기간교육 날짜
 */
export const displayPeriod = ({ isOnedayClass, startDate, startTime, endDate, endTime }) => {
  return isOnedayClass
    ? `${startDate} ${startTime} ~ ${endTime}`
    : `${startDate} ${startTime} ~ ${endDate.substring(5)} ${endTime}`
}

/**
 * @alias module:@helpers/i18n
 * @method formatDateTime
 * @param {*} param0 일자,
 * @returns {string} 포맷팅된 일자 시간
 */
export const formatDateTime = (dateTime) => {
  return `${toLocaleDateString(dateTime)} ${toLocaleTimeString(dateTime)}`
}

export const formatDateTimeWithFormat = (dateTime, dateFormat) => {
  return `${toLocaleDateStringWithFormat(dateTime, dateFormat)} ${toLocaleTimeString(dateTime)}`
}
